import axios from 'axios';


export interface DocumentType {
  id: number;
  name: string;
  created_at: string;
  updated_at: string;
}

export interface DocumentTypeReplaceParams {
  old_document_type: string;
  new_document_type: string;
}

export interface CourtType {
  id: number;
  name: string;
  created_at: string;
  updated_at: string;
}

export interface CourtTypeReplaceParams {
  old_court_type: string;
  new_court_type: string;
}

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors with concurrent refresh protection
let isRefreshing = false;
let failedQueue: { resolve: (token: string) => void; reject: (err: any) => void }[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (token) {
      resolve(token);
    } else {
      reject(error);
    }
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // If error is 401, not already retrying, and not from login/refresh endpoints
    if (
      error.response?.status === 401 && 
      !originalRequest._retry && 
      originalRequest.url !== '/api/auth/login' && 
      originalRequest.url !== '/api/auth/refresh'
    ) {
      // If already refreshing, queue this request to retry after refresh completes
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers['Authorization'] = `Bearer ${token}`;
          return api(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;
      
      try {
        const refreshToken = localStorage.getItem('refresh_token');
        if (!refreshToken) {
          throw new Error('No refresh token available');
        }
        
        // Use standard axios to prevent interceptor loops
        const response = await axios.post(`${api.defaults.baseURL || 'http://localhost:5001'}/api/auth/refresh`, {}, {
          headers: { 'Authorization': `Bearer ${refreshToken}` }
        });
        
        const { access_token } = response.data;
        
        // Update stored token
        localStorage.setItem('access_token', access_token);
        api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
        
        // Process all queued requests with the new token
        processQueue(null, access_token);
        
        // Retry the original request
        originalRequest.headers['Authorization'] = `Bearer ${access_token}`;
        return api(originalRequest);
        
      } catch (refreshError) {
        // Refresh failed — reject all queued requests and redirect to login
        processQueue(refreshError, null);
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        delete api.defaults.headers.common['Authorization'];
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
    
    return Promise.reject(error);
  }
);

// Publisher service functions
export const getPublishers = async (): Promise<string[]> => {
  const response = await api.get('/api/documents/publishers');
  return response.data.publishers;
};

// Organization service functions
export const getOrganizations = async (): Promise<string[]> => {
  const response = await api.get('/api/documents/organizations');
  return response.data.organizations;
};

export const getDocumentTypes = async (): Promise<DocumentType[]> => {
  const response = await api.get('/api/document-types');
  return response.data.documentTypes;
};


export const replaceDocumentType = async (params: DocumentTypeReplaceParams): Promise<{
  message: string;
  updated_documents_count: number;
}> => {
  const response = await api.post('/api/document-types/replace', params);
  return response.data;
};

export const getCourtTypes = async (): Promise<CourtType[]> => {
  const response = await api.get('/api/court-types');
  return response.data.courtTypes;
};

export const replaceCourtType = async (params: CourtTypeReplaceParams): Promise<{
  message: string;
  updated_documents_count: number;
}> => {
  const response = await api.post('/api/court-types/replace', params);
  return response.data;
};


export default api;
